import * as yup from 'yup';

import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { FC, ReactNode, useCallback, useEffect } from 'react';

import CustomizedCircularProgress from '@/components/shared/customized-circular-progress';
import OtpInput from 'react-otp-input';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useForm } from 'react-hook-form';
import { useOtpTimer } from '@/logic/state';
import { useTimer } from 'react-timer-hook';
import { yupResolver } from '@hookform/resolvers/yup';
import { BackwardButton } from './backward-button';
import { CloseButton } from './close-button';
import ImageBoxFuture from './image-box-future';

export interface VerificationCodeFormModel {
  code: string;
}

interface VerificationCodeFormProps {
  subtitle: string;
  description: string;
  isVerifyCodeLoading: boolean;
  error: boolean;
  title?: string;
  value?: string;
  errorMessage?: string;
  isBackwardEnabled?: boolean;
  handleFormClose: () => void;
  handleFormSubmit: (data: VerificationCodeFormModel) => void;
  handleCodeResend: () => void;
  actionComponent?: ReactNode;
  isSendOtpLoading: boolean;
}

export const VerificationCodeForm: FC<VerificationCodeFormProps> = ({
  title,
  subtitle,
  description,
  isVerifyCodeLoading,
  error,
  isBackwardEnabled,
  handleFormClose,
  handleFormSubmit,
  handleCodeResend,
  value,
  actionComponent,
  isSendOtpLoading,
  errorMessage,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const [otpTimer, setOtpTimer] = useOtpTimer(s => [s.otpTimer, s.setOtpTimer], shallow);

  const { seconds, minutes, restart, isRunning } = useTimer({
    expiryTimestamp: new Date(),
    autoStart: true, //set is running true in the beginning
  });

  const schema = yup.object({
    code: yup.string().required().max(4).min(4),
  });
  const {
    register,
    setValue,
    handleSubmit,
    trigger,
    getValues,
    formState: { errors, isSubmitted, dirtyFields },
  } = useForm<VerificationCodeFormModel>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  });

  const onSubmit = handleSubmit(handleFormSubmit);

  const startTime = useCallback(() => {
    const time = new Date();
    if (otpTimer?.timerVal > 0) {
      time.setSeconds(time.getSeconds() + (otpTimer?.timerVal as number));
      restart(time);
    }
  }, [restart, otpTimer?.timerVal]);

  const resendVerificationCode = useCallback(() => {
    handleCodeResend();
    const otpInput = document.querySelector('.otp-input') as HTMLElement;
    if (otpInput) {
      otpInput?.focus();
    }
    startTime();
  }, [handleCodeResend, startTime]);

  useEffect(() => {
    if (error) {
      setValue('code', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (otpTimer?.timerVal) startTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpTimer?.timerVal]);

  useEffect(() => {
    if (otpTimer?.timerVal > 0) {
      setOtpTimer({
        timerVal: otpTimer?.timerVal,
        isRunning,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning]);

  return (
    <>
      <DialogTitle>
        {isBackwardEnabled ? (
          <BackwardButton handleClick={handleFormClose} />
        ) : (
          <CloseButton handleClick={handleFormClose} />
        )}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '90%',
            margin: 'auto',
          }}
        >
          {title ? (
            <Typography
              component='div'
              variant='h4'
              sx={{
                textAlign: 'center',
                fontFamily: 'co-head-line-corp-bold',
                lineHeight: pxToRem(18),
                color: theme.palette.primary.main,
              }}
            >
              <FormattedMessage id={title} />
            </Typography>
          ) : (
            <ImageBoxFuture
              img='/icons/logos/muvi-logo.svg'
              title={intl.formatMessage({
                id: 'images.logo',
              })}
              objectFit='contain'
              sx={{
                width: '152px',
                height: '97px',
                alignSelf: 'center',
              }}
            />
          )}

          <Typography
            component='div'
            variant='h4'
            sx={{
              marginTop: '24px',
              textAlign: 'center',
              fontWeight: 700,
            }}
          >
            <FormattedMessage id={subtitle} />
          </Typography>
          <Typography
            component='div'
            variant='h6'
            sx={{
              direction: 'rtl',
              textAlign: 'center',
              mt: '5px',
              color: theme.palette.typography.color.muted,
            }}
          >
            <FormattedMessage id={description} values={{ value }} />
          </Typography>
        </Box>
        {isVerifyCodeLoading || isSendOtpLoading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 'auto',
              marginBlock: 3,
            }}
          >
            <CustomizedCircularProgress size={40} thickness={4} />
          </Box>
        ) : (
          <Box sx={{ mt: 7 }}>
            <FormControl
              variant='standard'
              id={'otp-input'}
              sx={{
                display: 'flex',
                justifyContent: 'center',

                '& .otp-input': {
                  width: '64px !important',
                  height: '64px',
                  borderRadius: '16px',
                  backgroundColor: theme.palette.layout.backgroundColor,
                  border: 'none',
                  outline: 'none',
                  fontSize: pxToRem(28),
                  fontWeight: 900,
                  color: theme.palette.typography.color.muted,
                },
              }}
            >
              {!isVerifyCodeLoading && (
                <OtpInput
                  {...register('code')}
                  value={getValues().code}
                  onChange={(value: string) => {
                    setValue('code', value);
                    trigger();
                    if (value.length === 4) onSubmit();
                  }}
                  isInputNum
                  shouldAutoFocus
                  separator={<Box sx={{ marginInline: pxToRem(20) }}></Box>}
                  containerStyle={{
                    margin: 'auto',
                    direction: 'ltr',
                  }}
                  inputStyle={'otp-input'}
                  focusStyle={{
                    border: `3px solid ${theme.palette.primary.main}`,
                  }}
                  errorStyle={{
                    border: `3px solid #E14851`,
                  }}
                  hasErrored={error && Boolean(dirtyFields) && isSubmitted}
                />
              )}
              <FormHelperText id='otp-error-message' error sx={{ mt: 1, ml: 3 }}>
                {(errors.code || error) &&
                  isSubmitted &&
                  (errorMessage ?? <FormattedMessage id='verification.code.not.valid' />)}
              </FormHelperText>
            </FormControl>
          </Box>
        )}
        {/*<Box>*/}
        {/*  {(isVerifyCodeLoading || isLoading) && (*/}
        {/*    <CircularProgress sx={{ margin: 'auto', marginBlock: 3, display: 'block' }} />*/}
        {/*  )}*/}
        {/*</Box>*/}
        <Stack direction='row' sx={{ mt: 4, justifyContent: 'center' }}>
          <Typography
            component='div'
            variant='h6'
            sx={{
              textAlign: 'center',
              color: theme.palette.typography.color.muted,
              fontSize: pxToRem(16),
            }}
          >
            <FormattedMessage id='did_not.receive.otp' />
          </Typography>
          {isRunning && !isSendOtpLoading && (
            <Typography
              component='div'
              variant='h6'
              sx={{
                textAlign: 'center',
                color: theme.palette.primary.main,
                fontSize: pxToRem(16),
              }}
            >
              &nbsp;
              {minutes < 10 ? `0${minutes}` : minutes}:
              {seconds < 10 ? `0${seconds}` : seconds}
            </Typography>
          )}
          {!isRunning && (
            <Typography
              id='resend-otp'
              component='div'
              variant='h6'
              onClick={() => resendVerificationCode()}
              sx={{
                textAlign: 'center',
                color: theme.palette.primary.main,
                fontSize: pxToRem(16),
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.primary.main,
                },
              }}
            >
              &nbsp;
              <FormattedMessage id='resend.otp' />
            </Typography>
          )}
        </Stack>
      </DialogContent>
      {actionComponent && (
        <DialogActions
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {actionComponent}
        </DialogActions>
      )}
    </>
  );
};
